import React, {memo, useCallback, useEffect, useMemo, useState} from "react";
import {DefaultContainer} from "@shared/DefaultContainer.tsx";
import {DefaultPageProps} from "@models/DefaultProps.ts";
import {IContratoParcela} from "@models/contratos/parcelamento.ts";
import {AddOutlined, SaveOutlined, EditOutlined} from "@mui/icons-material";
import dayjs from "dayjs";
import {useAlert} from "@contexts/hooks/useAlert.ts";
import {IContrato} from "@models/contratos/contrato.ts";
import {ToDateDefault, ToMoney} from "@shared/MathHelp.ts";
import usePagination from "@components/shared/hooks/usePagination.tsx";
import {useContrato} from "@components/contratos/hooks/useContrato.tsx";
import {useSearchParams} from "react-router";
import {ContratoParcelaRow} from "@components/contratos/parcelas/ContratoParcelaRow.tsx";
import {
	Button,
	ButtonGroup,
	Grid,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography
} from "@mui/material";
import {CreateParcelas} from "@components/contratos/parcelas/CreateParcelas.tsx";
import {IContratoDetalhes} from "@components/contratos/models/IContratoDetalhes.ts";


export type stateType = 'New' | 'Removed' | 'Updated' | 'Unchanged';

interface ContratoParcelasPageProps extends DefaultPageProps {
	preset?: {
		contrato?: number | IContrato;
		contratoDetalhes?: IContratoDetalhes
	}
	onSave?: (contrato: IContrato) => void;
}

export interface IContratoParcelaEntry extends IContratoParcela {
	state?: stateType
}


export function ContratoParcelasPage(props: ContratoParcelasPageProps) {
	const {preset} = props;
	const {getParcelas, updateAllParcelas, useContratoValue, getContratoDetalhesById} = useContrato()
	const {createAlertFromResponse, addPopUp, closePopUp} = useAlert()

	const [searchParams] = useSearchParams()
	const [contrato] = useContratoValue(props.preset?.contrato ?? Number(searchParams.get('contratoId')));
	const [detalhes, setDetalhes] = useState<IContratoDetalhes | undefined>(props.preset?.contratoDetalhes);
	const [parcelas, setParcelas] = useState<IContratoParcela[]>([]);
	const [toUpdate, setToUpdate] = useState<IContratoParcela[]>([]);
	const [toRemove, setToRemove] = useState<IContratoParcela[]>([]);
	const [isEditing, setIsEditing] = useState<boolean>(false);

	const contratoId = useMemo(() => {
		if (typeof preset?.contrato == 'number')
			return preset?.contrato
		else if (preset?.contrato)
			return preset?.contrato.id
		else
			return Number(searchParams.get('contratoId'))
	}, [props.preset?.contrato, searchParams])

	const toRemoveIds = useMemo(() => new Set(toRemove.map(x => x.id)), [toRemove])
	const MemoContratoParcelaRow = useMemo(() => memo(ContratoParcelaRow), [])

	const [pagination, setPagination] = usePagination({
		triggers: [
			{
				eventName: 'process',
				deps: [contrato?.id],
				promise: (p) => getParcelas({
					data: {idContrato: contratoId},
					pagination: p.pagination
				}).then((x) => {
					if (x.items != null) {
						setParcelas(x.items)
						setPagination(prev => ({...prev, ...x.pagination}))
					}
					return x;
				})
			}
		]
	});


	useEffect(() => {
		if (contrato?.id != null && detalhes == null) {
			getContratoDetalhesById({idContrato: contrato.id})
				.then(x => {
					if (x.items != null) {
						setDetalhes(x.items)
					}
				})
		}

	}, [contrato]);

	const enviarHandler = async () => {
		const handleSend = async () => {
			const removeIds = new Set(toRemove.map(x => x.id));
			const updatesSemRemovidos = toUpdate.filter(x => !removeIds.has(x.id));
			return await updateAllParcelas({
				idContrato: contratoId,
				atualizar: updatesSemRemovidos.map(x => ({
					parcelaId: x.id,
					valor: x.valor,
					dataVencimento: x.dataVencimento,
					observacao: x.observacao
				})),
				remover: toRemove.map(x => x.id)
			}).then(x => {
				if (x.status == 'Success') {
					setToRemove([])
					setToUpdate([])
					closePopUp()
					setPagination(prev => ({...prev, ignoreCache: true}))
					if (props.onSave) {
						props.onSave(x.items)
					}
				}
				createAlertFromResponse(x)
			})
		}

		if (toRemove.length > 0) {
			addPopUp({
				title: 'Cancelamento de parcelas',
				content: <Grid size={12} container>
					<Grid size={12}>
						<Typography>Essa ação irá cancelar {toRemove.length} parcelas, e é irreversivel, deseja
							continuar?</Typography>
					</Grid>
					<Grid size={12}>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell>Nro</TableCell>
									<TableCell>Valor</TableCell>
									<TableCell>Data Vencimento</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{toRemove.map((x) => (
									<TableRow>
										<TableCell>{x.numero}</TableCell>
										<TableCell>{x.valor}</TableCell>
										<TableCell>{ToDateDefault(x.dataVencimento)}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</Grid>
					<Grid size={12} container justifyContent={'flex-end'}>
						<ButtonGroup>
							<Button color={'warning'} onClick={handleSend}> Sim, cancele as parcelas </Button>
							<Button color={'info'} onClick={() => closePopUp()}> Não, voltar </Button>
						</ButtonGroup>
					</Grid>
				</Grid>
			})

			return
		}
		await handleSend()
	}

	const updateHandler = useCallback((parcela: IContratoParcela) => {
		setToUpdate((prev) => {
			const index = prev.findIndex(x => x.id == parcela.id)
			if (index != -1) {
				const newParcelas = prev.slice()
				newParcelas[index] = parcela
				return newParcelas
			}
			return [...prev, parcela]
		})
	}, [])

	const removeHandler = useCallback((parcela: IContratoParcela) => {
		setToRemove((prev) => {
			const exists = prev.some(x => x.id == parcela.id)
			const next = exists ? prev.filter(x => x.id != parcela.id) : [...prev, parcela];
			if (!exists) {
				// Se marcar para remoção, descarta alterações pendentes dessa parcela
				setToUpdate(prevU => prevU.filter(u => u.id != parcela.id))
			}
			return next
		})
	}, [])

	return (contrato != null &&
		<DefaultContainer component={Paper} permissaoNecessaria={['VisualizarParcelas']}
		                  pageProps={{pagination, setPagination}}
		                  UpperComponent={<Grid container justifyContent={'space-between'}>
			                  <Grid>
				                  <Typography>Parcelas {detalhes?.qtdParcelas} Total: {ToMoney(detalhes?.valorTotalParcelas)}</Typography>
			                  </Grid>
			                  <Grid container>
				                  <ButtonGroup>
					                  <Button endIcon={<AddOutlined/>}
					                          variant={'outlined'}
					                          onClick={() => {
						                          addPopUp({
							                          title: `Incluir nova parcela`,
							                          content: <CreateParcelas preset={{
								                          contrato: contrato,
								                          proximaData: dayjs(detalhes?.ultimaParcela).add(1, 'month')
							                          }} onCreate={() => {
								                          closePopUp()
							                          }}/>
						                          })
					                          }}>
						                  Incluir Parcelas
					                  </Button>
					                  <Button endIcon={<EditOutlined/>}
					                          disabled={!contrato.isDraft}
					                          color={'secondary'}
					                          onClick={() => setIsEditing(!isEditing)}>
						                  {isEditing ? 'Parar de editar' : 'Editar parcelas'}
					                  </Button>
					                  <Button endIcon={<SaveOutlined/>}
					                          color={'success'}
					                          onClick={enviarHandler}
					                          disabled={(!contrato.isDraft) || (toUpdate.length == 0 && toRemove.length == 0)}
					                          variant={'outlined'}>Salvar alterações</Button>
				                  </ButtonGroup>
			                  </Grid>
		                  </Grid>}>

			<TableContainer>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell>Nro</TableCell>
							<TableCell>Contrato</TableCell>
							<TableCell>Criado por</TableCell>
							<TableCell>Criado em</TableCell>
							<TableCell>Status</TableCell>
							<TableCell>Valor</TableCell>
							<TableCell>Valor Pago</TableCell>
							<TableCell>Data Vencimento</TableCell>
							<TableCell>Observações</TableCell>
							<TableCell>Mais</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{parcelas.map((parcela) => (
							<MemoContratoParcelaRow editing={isEditing}
							                        value={parcela}
							                        key={parcela.id}
							                        removing={toRemoveIds.has(parcela.id)}
							                        onChange={updateHandler}
							                        onRemove={removeHandler}/>
						))}
					</TableBody>
				</Table>
			</TableContainer>
		</DefaultContainer>
	)
}